// 共通表示枠（臨時メッセージ欄）。wuSiteResource2 の Shared/Notices.ts（Issue #15）の移植。
//
// ページに埋め込まれた <div data-wu-notice="キー名"></div> を集め、サーバーが返す
// 「いま有効な全告知」のうちキーが一致するものをその枠に差し込む。
//
// サーバーはキーで絞り込まない(いま有効な全件を返す)ので、キー一致の判定はこちら側の担当。
// 同じキーの枠がページ内に複数あれば、そのすべてに差し込む(DOM順・重複表示は許容の仕様)。
// 枠が1つも無ければAPIは叩かない（hooks.ts の when() が担う）。

/** 告知1件。サーバーの返す JSON の形。 */
export interface Notice {
  key: string
  html: string
}

/** キーとして認めるのは [a-z0-9-] の1〜64文字（仕様）。 */
export const NOTICE_KEY_PATTERN = /^[a-z0-9-]{1,64}$/

const DEFAULT_API = 'https://front.workup-system.com/api/notices'

/**
 * 枠の中身が空とみなせるか。
 *
 * HTMLコメント(`<!-- 告知が入ります -->` のような目印)とゼロ幅文字は、あっても「空」として扱う。
 * これらは JS の `\s` に当たらないので単純な空白除去では残ってしまい、
 * 埋め込み方がほんの少し違うだけで告知が黙って出なくなる。
 */
// U+200B ZERO WIDTH SPACE / U+200C ZWNJ / U+200D ZWJ / U+FEFF BOM。
// リテラルで書くとソース上で見えない文字になるため、\u エスケープで書く。
const BLANKABLE = new RegExp('[\\s\\u200B\\u200C\\u200D\\uFEFF]', 'g')

export function isBlank(html: string): boolean {
  return html.replace(/<!--[\s\S]*?-->/g, '').replace(BLANKABLE, '') === ''
}

/**
 * ページ内の枠を data-wu-notice の値ごとに集める。
 *
 * 属性名は `data-wu-notice` で固定（旧 `data-type` は非対応）。汎用的な属性名だと
 * おちゃのこのテーマや外部SDKと衝突する——実際に LINE のシェアボタン
 * (`<div class="line-it-button" style="display:none" data-type="share-a"></div>`) が
 * 「div・中身が空・キー文字種一致」の3条件をすべて満たしていた（SDKが後から中身を埋める作り）。
 * 形での選別では防げず、告知キーがたまたま `share-a` だとシェアボタンを壊す。
 * → 属性名を名前空間化して原理的に衝突を消してある（2026-08-07 Core2側と合意）。
 *
 * 「div であること／中身が空であること」という形の条件は残っているが、役割は衝突対策ではなく
 * **一括埋め込みの事故対策**。数千商品のHTML書き換えで枠が既存の商品説明を巻き込んでしまった
 * 場合に、上書きせず被害を止める。埋め込み側のルールは「枠の中に文言・画像を入れない」。
 */
export function collectNoticeFrames(root: ParentNode): Record<string, HTMLElement[]> {
  const frames: Record<string, HTMLElement[]> = {}
  root.querySelectorAll<HTMLElement>('div[data-wu-notice]').forEach((element) => {
    if (!isBlank(element.innerHTML)) return
    const key = element.getAttribute('data-wu-notice')
    if (key == null || !NOTICE_KEY_PATTERN.test(key)) return
    ;(frames[key] ??= []).push(element)
  })
  return frames
}

/**
 * 叩き先のAPIを決める。既定は本番だが、枠のどれかに `data-api` があればそちらを使う
 * （ローカル検証・検証台のためのもの。widgetの `data-api` と同じ作法——order-status/
 * product-review/stock-shortage を参照。枠はウィジェットの箱を持たない hook なので、
 * 設定を置く先が無く、収集した枠自身の属性を流用する）。
 * 障害時のフォールバック先としては使わない——**戻し先は持たず、ここを直す**。
 */
export function resolveApi(frames: Record<string, HTMLElement[]>): string {
  for (const targets of Object.values(frames)) {
    for (const el of targets) {
      const api = el.dataset.api
      if (api) return api
    }
  }
  return DEFAULT_API
}

/** キー一致する notice を、対応する枠すべてへ差し込む。 */
export function applyNotices(frames: Record<string, HTMLElement[]>, notices: Notice[]): void {
  for (const notice of notices) {
    if (notice == null || notice.key == null || notice.html == null) continue
    const targets = frames[notice.key]
    if (targets == null) continue
    for (const target of targets) target.innerHTML = notice.html
  }
}

/** 1ページで二重に走らせないための番人。hooks.ts 側にも RAN があるが、直接呼ぶ経路のための保険。 */
let started = false

export default async function setupNotices(): Promise<void> {
  if (started) return
  started = true

  try {
    const frames = collectNoticeFrames(document)
    if (Object.keys(frames).length === 0) return // 枠が1つも無ければAPIを叩かない

    const siteInfoId = window.wu?.siteInfoId ?? ''
    // data-api にローカル検証用の相対パスを渡せるよう、location を base にする
    // （本番は絶対URLが解決される想定で base は無視される。product-review 等と同じ作法）。
    const url = new URL(resolveApi(frames), location.href)
    url.searchParams.set('siteInfoId', String(siteInfoId))
    // url は現時点でサーバーが判断に使わないが、後から特殊動作を足すため必ず送る。
    url.searchParams.set('url', location.href)

    const r = await fetch(url, { cache: 'no-store' })
    if (!r.ok) throw new Error(`status ${r.status}`)
    const notices = (await r.json()) as unknown
    if (!Array.isArray(notices)) return

    applyNotices(frames, notices as Notice[])
  } catch (e) {
    console.error('[wu] notices の取得に失敗しました', e)
  }
}
